/**
 * 担当案件一覧の「開発 / デザイン」view 切替 pill スイッチ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 担当案件一覧の開発/デザイン view 切替スイッチ（開発=ライト青/ダーク黄 / デザイン=桜色、切替時にアクティブ色がグラデーション遷移）
 * @graph-connects none
 */
import { useTranslation } from 'react-i18next';

/** 担当案件一覧の表示 view（開発 / デザイン）。 */
export type BoardView = 'dev' | 'design';

interface DevDesignViewSwitchProps {
  /** 現在の表示 view */
  view: BoardView;
  /** view 変更コールバック */
  onChange: (next: BoardView) => void;
}

/**
 * 開発 / デザイン（桜色のピンク）の view を切替える pill スイッチ。
 * アクティブ側に色が乗り、`transition-colors` で切替時に色が滑らかに変化する。
 *
 * 開発側の色はテーマで変わる（ライト = 従来の青 / ダーク = 黄）。トークンは styles.css の
 * `--color-dev-accent*` が SSOT で、ここで Tailwind の `blue-*` を直に書くと
 * ダークでも青のままになる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 担当案件一覧ヘッダーの開発/デザイン view 切替 UI（テキストのみ・アイコンなし）
 * @graph-connects none
 */
export function DevDesignViewSwitch({ view, onChange }: DevDesignViewSwitchProps) {
  const { t } = useTranslation();
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-0.5"
      role="group"
      aria-label={t('myProjectsBoard.viewSwitch')}
    >
      <button
        type="button"
        aria-pressed={view === 'dev'}
        onClick={() => {
          onChange('dev');
        }}
        className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors duration-300 ${
          view === 'dev'
            ? 'bg-[var(--color-dev-accent)] text-[var(--color-dev-accent-on)]'
            : 'text-[var(--color-dev-accent-text)] hover:text-[var(--color-dev-accent-hover)]'
        }`}
      >
        {t('myProjectsBoard.viewDev')}
      </button>
      <button
        type="button"
        aria-pressed={view === 'design'}
        onClick={() => {
          onChange('design');
        }}
        className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors duration-300 ${
          view === 'design' ? 'bg-pink-300 text-pink-900' : 'text-pink-400 hover:text-pink-500'
        }`}
      >
        {t('myProjectsBoard.viewDesign')}
      </button>
    </div>
  );
}
